/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, forwardRef, useRef } from 'react';
import * as Highcharts from 'highcharts';
// import Highcharts from 'highcharts/es-modules/masters/highcharts.src.js';
import _ from 'lodash';
import D_Currency from '@utils/currency.js';
import D_Number from '@utils/number.js';

import classnames from 'classnames';
import useTheme from '@hooks/useTheme';
import clear_filter_icon from '@assets/imgs/icons/clear_filter.png';
import { xAxisStyleDefault } from './config';
import { NoDataWithName } from '@components';
import { defaultToolTip } from './config';

const defaultBarChartWithLineOption = (metrics, currentCashType) => ({
  chart: {
    type: 'column',
    style: {
      'font-family': 'Helvetica,Arial',
    },
    // height: 350,
  },
  title: {
    align: 'left',
    style: {
      'font-family': ' HelveticaNeue-Medium,Arial',
      'font-size': '1rem',
      color: '#DBDBDB !important',
    },
    margin: 30,
  },
  colors: ['#0081E9'],
  tooltip: {
    ...defaultToolTip,
  },
  xAxis: {
    lineColor: '#666',
    labels: {
      style: xAxisStyleDefault,
      autoRotation: false,
      // formatter() {
      //   return this.value.replace(', 20', '');
      // },
    },
    // crosshair: true,
  },
  yAxis: [
    {
      title: {
        text: undefined,
      },
      gridLineColor: '#666',
      gridLineDashStyle: 'longdash',
      labels: {
        formatter() {
          return D_Currency.format(
            {
              value: this.value,
              currencyIsoCode: currentCashType[0],
            },
            0,
          );
        },
        style: {
          'font-family': 'HelveticaNeue,Arial',
          'font-size': '0.875rem',
          color: '#333333 !important',
        },
      },
      softMin: 0,
    },
    {
      title: {
        text: undefined,
      },
      gridLineColor: '#666',
      gridLineDashStyle: 'longdash',
      labels: {
        formatter() {
          if (metrics === 'eps' || metrics === 'market Cap') {
            return D_Currency.format(
              {
                value: this.value,
                currencyIsoCode: currentCashType[1],
              },
              0,
            );
          }
          // console.log(this.value);
          return `${D_Number.formatPercentage(this.value, 0)}`;
        },
        style: {
          'font-family': 'HelveticaNeue,Arial',
          'font-size': '0.875rem',
          color: '#333333 !important',
        },
      },
      opposite: true,
      softMin: 0,
    },
  ],
  series: [],
  legend: {
    symbolHeight: 10,
    symbolWidth: 10,
    symbolRadius: 0,
    itemStyle: {
      'font-family': 'HelveticaNeue,Arial',
      'font-weight': 'normal',
      'font-size': '0.75rem',
      color: '#333333 !important',
    },
    reversed: true,
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    column: {
      maxPointWidth: 60,
    },
    line: {
      marker: {
        symbol: 'circle',
      },
    },
    series: {
      events: {
        // legendItemClick: () => false,
        legendItemClick() {
          if (this.name === 'Show All') {
            this.chart.series.forEach((series) => {
              if (series.name !== this.name) {
                series.setVisible(true, false);
              }
            });
            this.chart.redraw();
            return false;
          }
          const showModeOn = this.chart.series.some(
            (series) => series.visible === false,
          );
          if (showModeOn) {
            return true;
          }
          const clickedSeries = this;
          this.chart.series.forEach((series) => {
            if (series.name !== this.name && series.name !== 'Show All') {
              series.setVisible(!series.visible, false);
            }
          });
          this.chart.redraw();
          return false;
        },
      },
    },
  },
});

const combineBarChartWithLineOption = (
  dynamicBarChartWithLineOption,
  basicBarChartWithLineOption,
) => {
  let finalOption = { ...basicBarChartWithLineOption }; // Create a copy first

  // Ensure yAxis is properly initialized
  if (!finalOption.yAxis || !Array.isArray(finalOption.yAxis)) {
    finalOption.yAxis = [];
  }

  const dynamicOptions = Object.keys(dynamicBarChartWithLineOption);
  for (let i = 0; i < dynamicOptions.length; i++) {
    const optionKey = dynamicOptions[i];
    if (optionKey in basicBarChartWithLineOption) {
      if (basicBarChartWithLineOption[optionKey] instanceof Array) {
        finalOption = {
          ...finalOption,
          [optionKey]:
            dynamicBarChartWithLineOption[optionKey] ||
            basicBarChartWithLineOption[optionKey],
        };
      } else {
        finalOption = {
          ...finalOption,
          [optionKey]: {
            ...finalOption[optionKey],
            ...dynamicBarChartWithLineOption[optionKey],
          },
        };
      }
    }
  }
  return finalOption;
};

const buildDynamicBarChartWithLineOption = (
  propsOptions,
  metrics,
  currentCashType,
) => {
  let finalDynamicOption = {};
  if ('containerId' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      chart: { renderTo: propsOptions.containerId },
    };
  }
  if ('companyName' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      title: {
        text: propsOptions.companyName,
      },
    };
  }
  if ('categories' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      xAxis: {
        categories: propsOptions.categories,
      },
    };
  }
  if ('series' in propsOptions) {
    const defaultSeriesOption = [
      {
        borderColor: 'transparent',
        borderRadius: 2,
        type: 'column',
        tooltip: {
          headerFormat: null,

          pointFormatter() {
            const str =
              `<b>${this.category.replace(', 20', '')}</b>` +
              '<br/>' +
              `<b>${this.series.name}</b>: ${D_Currency.format({
                value: this.y,
                currencyIsoCode: currentCashType[0],
              })}<br/>`;

            return str;
          },
        },
      },
      {
        borderColor: 'transparent',
        borderRadius: 2,
        type: 'line',
        yAxis: 1,
        color: '#FFB600',
        tooltip: {
          headerFormat: null,
          pointFormatter() {
            const str =
              `<b>${this.category.replace(', 20', '')}</b>` +
              '<br/>' +
              `<b>${this.series.name}</b>: ${
                metrics !== 'eps' && metrics !== 'market Cap'
                  ? `${D_Number.formatPercentage(this.y, 1)}`
                  : `${D_Currency.format({
                      value: this.y,
                      currencyIsoCode: currentCashType[1],
                    })}`
              }`;

            return str;
          },
        },
      },
    ];
    finalDynamicOption = {
      ...finalDynamicOption,
      series: propsOptions.series.map((seryItem, idx) => ({
        ...seryItem,
        ...defaultSeriesOption[idx],
      })),
    };
  }
  if ('secondaryXAxis' in propsOptions) {
    finalDynamicOption.xAxis.title = {
      text: propsOptions.secondaryXAxis,
      margin: 15,
    };
  }
  return finalDynamicOption;
};

const buildBarChartWithLineOption = (
  propsOptions,
  metrics,
  currentCashType = [],
) => {
  const dynamicBarChartWithLineOption = buildDynamicBarChartWithLineOption(
    propsOptions,
    metrics,
    currentCashType,
  );
  const finalOption = combineBarChartWithLineOption(
    dynamicBarChartWithLineOption,
    defaultBarChartWithLineOption(metrics, currentCashType),
  );

  // ✅ Add comprehensive null checks
  if ('yMax' in propsOptions && propsOptions.yMax) {
    // Ensure yAxis exists and has required elements
    if (!finalOption.yAxis || !Array.isArray(finalOption.yAxis)) {
      console.warn('yAxis not properly initialized in BarChartWithLine');
      finalOption.yAxis = [
        {
          title: { text: undefined },
          gridLineColor: '#666',
          gridLineDashStyle: 'longdash',
          labels: {
            formatter() {
              return D_Currency.format(
                {
                  value: this.value,
                  currencyIsoCode: currentCashType[0],
                },
                0,
              );
            },
            style: {
              'font-family': 'HelveticaNeue,Arial',
              'font-size': '0.875rem',
              color: '#333333 !important',
            },
          },
          softMin: 0,
        },
        {
          title: { text: undefined },
          gridLineColor: '#666',
          gridLineDashStyle: 'longdash',
          labels: {
            formatter() {
              if (metrics === 'eps' || metrics === 'market Cap') {
                return D_Currency.format(
                  {
                    value: this.value,
                    currencyIsoCode: currentCashType[1],
                  },
                  0,
                );
              }
              return `${D_Number.formatPercentage(this.value, 0)}`;
            },
            style: {
              'font-family': 'HelveticaNeue,Arial',
              'font-size': '0.875rem',
              color: '#333333 !important',
            },
          },
          opposite: true,
          softMin: 0,
        },
      ];
    }

    // Ensure we have at least 2 yAxis elements
    if (finalOption.yAxis.length < 2) {
      console.warn('Not enough yAxis elements, adding missing ones');
      while (finalOption.yAxis.length < 2) {
        finalOption.yAxis.push({
          title: { text: undefined },
          gridLineColor: '#666',
          gridLineDashStyle: 'longdash',
          labels: {
            style: {
              'font-family': 'HelveticaNeue,Arial',
              'font-size': '0.875rem',
              color: '#333333 !important',
            },
          },
          opposite: finalOption.yAxis.length === 1,
          softMin: 0,
        });
      }
    }

    // Now safely access yAxis elements
    if (propsOptions.yMax.leftMax != null) {
      const leftSuffix = D_Currency.format({
        value: propsOptions.yMax.leftMax,
      }).slice(-1);
      finalOption.yAxis[0].softMax = propsOptions.yMax.leftMax;
      finalOption.yAxis[0].softMin = propsOptions.yMax.leftMin;
      finalOption.yAxis[0].labels.formatter = function () {
        return D_Currency.formatWithCertainSuffix(
          this.value,
          leftSuffix,
          1,
          currentCashType[0],
        );
      };
    }

    if (propsOptions.yMax.rightMax != null) {
      const rightSuffix = D_Currency.format({
        value: propsOptions.yMax.rightMax,
      }).slice(-1);
      finalOption.yAxis[1].softMax = propsOptions.yMax.rightMax;
      finalOption.yAxis[1].softMin = propsOptions.yMax.rightMin;
      finalOption.yAxis[1].labels.formatter = function () {
        if (metrics === 'eps' || metrics === 'market Cap') {
          return D_Currency.formatWithCertainSuffix(
            this.value,
            rightSuffix,
            1,
            currentCashType[1],
          );
        }
        return `${D_Number.formatPercentage(this.value, 0)}`;
      };
    }
  }

  return finalOption;
};

const BarChartWithLine = forwardRef(
  (
    {
      containerId,
      companyName,
      categories,
      series,
      metrics,
      secondaryXAxis,
      noDataFlag,
      yMax,
    },
    ref,
  ) => {
    console.log('BarChartWithLine Props:', {
      containerId,
      companyName,
      categories,
      series,
      metrics,
      secondaryXAxis,
      noDataFlag,
      yMax,
    });

    const noData =
      _.compact(_.flatten(series.map((item) => item.data || []))).length ===
        0 || noDataFlag;

    console.log('BarChartWithLine Computed noData flag:', noData);
    console.log(
      'BarChartWithLine Series data flattened:',
      _.compact(_.flatten(series.map((item) => item.data || []))),
    );

    const { theme } = useTheme();
    const isLightMode = theme === 'light';

    const clearFilters = (chart, i) => {
      try {
        chart.series.forEach((series) => {
          series.setVisible(true);
        });
        filterImageRef.current && filterImageRef.current.hide();
      } catch (_unused) {
        console.log('not have chart');
      }
    };

    const filterImageRef = useRef({});

    const showHideFilterIcon = (chart, event) => {
      if (!chart) {
        return;
      }
      const legendItems = _.cloneDeep(chart.legend.allItems);
      if (legendItems && legendItems.length) {
        let hasHide = false;
        for (let i = 0; i < legendItems.length; i++) {
          if (event && legendItems[i].name === event.target.name && !hasHide) {
            hasHide = legendItems[i].visible;
          } else if (!legendItems[i].visible && !hasHide) {
            hasHide = true;
          }
        }
        if (hasHide) {
          filterImageRef.current && filterImageRef.current.show();
        } else {
          filterImageRef.current && filterImageRef.current.hide();
        }
      }
    };

    const highchartsCallback = (chart, i) => {
      filterImageRef.current = chart.renderer
        .image(clear_filter_icon, 15, chart.chartHeight - 39, 14, 14)
        .on('click', () => {
          clearFilters(chart, i);
        })
        .on('mouseover', function (e) {
          const tooltipDom = document.querySelector(
            `.chart-content-${containerId}`,
          ).firstElementChild;
          if (!tooltipDom.classList.contains('tooltip-visibility')) {
            const chartOffsetLeft =
              (chart.container && chart.container.offsetLeft) || 0;
            tooltipDom.classList.add('tooltip-visibility');
            tooltipDom.style.top = `${e.layerY - 50}px`;
            tooltipDom.style.left = `${e.layerX - 38 + chartOffsetLeft}px`;
          }
        })
        .on('mouseout', () => {
          document
            .querySelector(`.chart-content-${containerId}`)
            .firstElementChild.classList.remove('tooltip-visibility');
          // chart.clearFilterTooltip && chart.clearFilterTooltip.destroy();
        })
        .css({
          width: '0.875rem',
          height: '0.875rem',
          cursor: 'pointer',
        })
        .add();
      filterImageRef.current && filterImageRef.current.hide();
    };

    useEffect(() => {
      // const _series = series.map((s) => ({
      //   ...s,
      //   data: s.data.map((data) => (data === 0 ? null : data)),
      // }));

      try {
        if (!series || series.length === 0 || !containerId) {
          console.log(
            'Early return - missing required props for BarChartWithLine',
          );
          return;
        }

        const barChartWithLineOption = buildBarChartWithLineOption(
          {
            containerId,
            companyName,
            categories,
            series,
            secondaryXAxis,
            yMax,
          },
          metrics,
          series.length ? series.map((item) => item.unitType) : [],
        );

        // barChartWithLineOption.legend.alignColumns = false;
        barChartWithLineOption.legend.title = {
          text: barChartWithLineOption.xAxis.title.text
            ? `<span style="font-size: 0.875rem; color: #666; font-weight: normal">${barChartWithLineOption.xAxis.title.text}</span>`
            : '',
          style: {
            fontStyle: 'normal',
          },
        };
        barChartWithLineOption.xAxis.title.text = '';
        barChartWithLineOption.chart.events = {
          render() {
            const legend = this.legend;
            const title = legend.title;
            if (title) {
              const legendWidth = legend.legendWidth;
              const titleWidth = title.width;
              const diff = legendWidth - titleWidth;
              let offsetX = diff / 2;
              if (titleWidth >= legendWidth - 8) {
                offsetX -= diff;
              }
              title.attr({ x: offsetX });
            }
          },
        };

        barChartWithLineOption.plotOptions = {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: false,
            },
          },
          series: {
            events: {
              legendItemClick(event) {
                showHideFilterIcon(this.chart, event);
              },
            },
          },
        };

        if (typeof Highcharts === 'object' && Highcharts.chart) {
          // Destroy existing chart if it exists
          if (ref.current) {
            ref.current.destroy();
          }

          const barChartWithLine = Highcharts.chart(
            containerId,
            barChartWithLineOption,
            highchartsCallback,
          );

          ref.current = barChartWithLine;
        }
      } catch (error) {
        console.error('Error creating BarChartWithLine:', error);
      }
    }, [containerId, companyName, categories, series, yMax]);

    return (
      <div
        className={`chart-content 
        ${classnames(
          'gantt-chart-content',
          'clear-filter-tooltip',
          `chart-content-${containerId}`,
          {
            'highcharts-dark': !isLightMode,
            'highcharts-light': isLightMode,
          },
        )}`}
      >
        <p className="tooltip-text">Clear Filter</p>
        {noData && (
          <NoDataWithName key={`key-${containerId}`} name={companyName} />
        )}
        <div id={containerId} style={{ display: noData ? 'none' : '' }} />
      </div>
    );
  },
);

export default React.memo(BarChartWithLine);
